// Image utility functions

/**
 * ตรวจสอบประเภทไฟล์รูปภาพ
 * @param {File} file - ไฟล์ที่ต้องการตรวจสอบ
 * @returns {boolean} - true ถ้าเป็นไฟล์รูปภาพ
 */
export const isValidImageFile = (file) => {
  const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
  return validTypes.includes(file.type);
};

/**
 * ตรวจสอบขนาดไฟล์
 * @param {File} file - ไฟล์ที่ต้องการตรวจสอบ
 * @param {number} maxSizeMB - ขนาดสูงสุด (MB)
 * @returns {boolean} - true ถ้าขนาดไฟล์ไม่เกินกำหนด
 */
export const isValidFileSize = (file, maxSizeMB = 5) => {
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  return file.size <= maxSizeBytes;
};

/**
 * บีบอัดรูปภาพ
 * @param {File} file - ไฟล์รูปภาพ
 * @param {number} maxWidth - ความกว้างสูงสุด
 * @param {number} maxHeight - ความสูงสูงสุด
 * @param {number} quality - คุณภาพ (0-1)
 * @returns {Promise<File>} - ไฟล์ที่บีบอัดแล้ว
 */
export const compressImage = (file, maxWidth = 800, maxHeight = 600, quality = 0.8) => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      // คำนวณขนาดใหม่
      let { width, height } = img;
      
      if (width > height) {
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
      } else {
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }
      }

      canvas.width = width;
      canvas.height = height;

      // วาดรูปใหม่
      ctx.drawImage(img, 0, 0, width, height);

      // แปลงเป็น Blob
      canvas.toBlob(
        (blob) => {
          const compressedFile = new File([blob], file.name, {
            type: file.type,
            lastModified: Date.now(),
          });
          resolve(compressedFile);
        },
        file.type,
        quality
      );
    };

    img.src = URL.createObjectURL(file);
  });
};

/**
 * สร้าง thumbnail
 * @param {File} file - ไฟล์รูปภาพ
 * @param {number} size - ขนาด thumbnail
 * @returns {Promise<string>} - URL ของ thumbnail
 */
export const createThumbnail = (file, size = 150) => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      canvas.width = size;
      canvas.height = size;

      // วาดรูปเป็นสี่เหลี่ยมจัตุรัส
      ctx.drawImage(img, 0, 0, size, size);

      resolve(canvas.toDataURL('image/jpeg', 0.7));
    };

    img.src = URL.createObjectURL(file);
  });
};

/**
 * ตรวจสอบจำนวนรูปที่สามารถอัพโหลดได้ตาม Tier
 * @param {string} tier - Tier ของผู้ใช้
 * @param {string} userType - ประเภทผู้ใช้ ('girl' | 'customer' | 'manager')
 * @returns {number} - จำนวนรูปที่สามารถอัพโหลดได้
 */
export const getMaxImagesByTier = (tier, userType) => {
  if (userType !== 'girl') return 0;

  const tierLimits = {
    free: 1,
    basic: 3,
    premium: 6,
    vip: -1, // ไม่จำกัด
  };

  return tierLimits[tier] || 0;
};

/**
 * ตรวจสอบสิทธิ์การดูรูปตาม Tier
 * @param {string} tier - Tier ของผู้ใช้
 * @param {string} userType - ประเภทผู้ใช้
 * @returns {object} - ข้อมูลสิทธิ์การดูรูป
 */
export const getViewPermissionsByTier = (tier, userType) => {
  if (userType === 'girl') {
    return {
      canViewOwnImages: true,
      canViewAllImages: false,
      dailyViewLimit: -1,
    };
  }

  if (userType === 'customer') {
    const customerLimits = {
      free: { dailyViewLimit: 10, canViewAllImages: false },
      basic: { dailyViewLimit: 20, canViewAllImages: true },
      silver: { dailyViewLimit: 100, canViewAllImages: true },
      gold: { dailyViewLimit: 300, canViewAllImages: true },
      platinum: { dailyViewLimit: -1, canViewAllImages: true },
    };

    return customerLimits[tier] || customerLimits.free;
  }

  if (userType === 'manager') {
    return {
      canViewOwnImages: true,
      canViewAllImages: true,
      dailyViewLimit: -1,
    };
  }

  return {
    canViewOwnImages: false,
    canViewAllImages: false,
    dailyViewLimit: 0,
  };
};

/**
 * สร้างชื่อไฟล์ที่ไม่ซ้ำ
 * @param {string} userId - ID ของผู้ใช้
 * @param {string} originalName - ชื่อไฟล์เดิม
 * @returns {string} - ชื่อไฟล์ใหม่
 */
export const generateUniqueFileName = (userId, originalName) => {
  const timestamp = Date.now();
  const randomString = Math.random().toString(36).substring(2, 8);
  const extension = originalName.split('.').pop();
  return `${userId}_${timestamp}_${randomString}.${extension}`;
};

/**
 * ตรวจสอบว่าสามารถอัพโหลดรูปเพิ่มได้หรือไม่
 * @param {number} currentCount - จำนวนรูปปัจจุบัน
 * @param {number} maxCount - จำนวนสูงสุด
 * @returns {boolean} - true ถ้าสามารถอัพโหลดเพิ่มได้
 */
export const canUploadMore = (currentCount, maxCount) => {
  if (maxCount === -1) return true; // ไม่จำกัด
  return currentCount < maxCount;
};

/**
 * คำนวณขนาดไฟล์ที่เหมาะสมสำหรับแสดงผล
 * @param {number} bytes - ขนาดไฟล์เป็น bytes
 * @returns {string} - ขนาดไฟล์ที่อ่านได้
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * ตรวจสอบว่ารูปภาพมีคุณภาพดีหรือไม่
 * @param {File} file - ไฟล์รูปภาพ
 * @returns {Promise<boolean>} - true ถ้ารูปภาพมีคุณภาพดี
 */
export const checkImageQuality = (file) => {
  return new Promise((resolve) => {
    const img = new Image();
    
    img.onload = () => {
      // ตรวจสอบขนาดขั้นต่ำ
      const minWidth = 300;
      const minHeight = 300;
      
      if (img.width < minWidth || img.height < minHeight) {
        resolve(false);
        return;
      }
      
      // ตรวจสอบอัตราส่วน
      const aspectRatio = img.width / img.height;
      const validRatios = [0.5, 0.75, 1, 1.33, 1.5, 2]; // อัตราส่วนที่ยอมรับได้
      
      const isCloseToValidRatio = validRatios.some(ratio => 
        Math.abs(aspectRatio - ratio) < 0.1
      );
      
      resolve(isCloseToValidRatio);
    };
    
    img.onerror = () => resolve(false);
    img.src = URL.createObjectURL(file);
  });
};

/**
 * สร้างข้อมูลรูปภาพสำหรับเก็บในฐานข้อมูล
 * @param {string} url - URL ของรูปภาพ
 * @param {string} fileName - ชื่อไฟล์
 * @param {string} originalName - ชื่อไฟล์เดิม
 * @param {string} status - สถานะ ('pending' | 'approved' | 'rejected')
 * @returns {object} - ข้อมูลรูปภาพ
 */
export const createImageData = (url, fileName, originalName, status = 'pending') => {
  return {
    url,
    fileName,
    originalName,
    status,
    uploadedAt: new Date().toISOString(),
    views: 0,
    likes: 0,
  };
};









